import React from 'react';
import Card from '../components/card/Card';
import P from '../components/texts/p/P';
import Icon from '../components/icon/Icon';
import Tooltip from '../components/tooltip/Tooltip';
import OverdueBadge from '../components/badge/OverdueBadge';
import { getFiscalIdConfig } from '../utils/fiscalIdentifier';

const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

const cellShellStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
  boxSizing: 'border-box',
};

const centerCell = (children: React.ReactNode) => (
  <div style={cellShellStyle}>{children}</div>
);

const ellipsisTextStyle: React.CSSProperties = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '100%',
  textAlign: 'right',
  margin: 0,
  width: 0,
  minWidth: '100%',
};

const truncateWrapperStyle: React.CSSProperties = {
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
  display: 'grid',
};

const truncateCell = (text: string | null | undefined, fallback = '-') => {
  const display = text?.trim() ? text : fallback;
  const tooltipMsg = text?.trim() ? text : '';
  return (
    <div style={truncateWrapperStyle}>
      <Tooltip position='top' msg={tooltipMsg}>
        <P type='3' variant='neutral' withMargin={false} style={ellipsisTextStyle}>
          {display}
        </P>
      </Tooltip>
    </div>
  );
};

const simpleCell = (content: React.ReactNode) =>
  centerCell(
    <P type='3' variant='neutral' withMargin={false} style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
      {content}
    </P>
  );

const sortCol = (sortField: string, extra: Record<string, unknown> = {}) => ({
  sortable: true,
  sortField,
  id: sortField,
  ...extra,
});
const headerWithTooltip = (shortLabel: string, fullLabel: string) => (
  <Tooltip position='top' msg={fullLabel}>
    <span>{shortLabel}</span>
  </Tooltip>
);

const sortableHeader = (
  label: React.ReactNode,
  active: boolean,
  direction: 'asc' | 'desc' | null | undefined
) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
    {label}
    <span
      aria-hidden
      style={{
        fontSize: '10px',
        lineHeight: 1,
        color: active ? '#374151' : '#9CA3AF',
        opacity: active ? 1 : 0.45,
      }}
    >
      {active && direction === 'asc' ? '▲' : '▼'}
    </span>
  </span>
);

const abbreviateCurrency = (currency: string | null | undefined): string => {
  if (!currency?.trim()) return '-';
  const lower = currency.toLowerCase();
  if (lower.includes('uruguay') || lower === 'uyu') return 'UYU';
  if (lower.includes('peso') || lower === 'ars' || lower === '$') return 'ARS';
  if (lower.includes('dolar') || lower.includes('dólar') || lower === 'usd' || lower.includes('u$s')) {
    return 'USD';
  }
  return currency;
};

export const formatOverdueGridAmount = (value: number | null | undefined): string => {
  const n = value ?? 0;
  return n.toLocaleString('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const amountCellStyle: React.CSSProperties = {
  whiteSpace: 'nowrap',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};

export type BadgeStatus =
  | 'PENDIENTE'
  | 'VENCIDA'
  | 'AFRONTADA'
  | 'HONRADA_PARCIAL'
  | 'HONRADA'
  | 'COBRADA_OK'
  | 'INCOBRABLE'
  | 'CANCELADO_ANTICIPADAMENTE';

export interface OverdueData {
  id: number;
  row_key?: string;
  id_global: string;
  type: string;
  application_product_line_id: number;
  due_date: string;
  days_overdue: number;
  monetization_amount: number;
  installment_number: number;
  total_installments: number;
  creditor_name: string;
  currency: string;
  cuit: string;
  company_name: string;
  rate_type_id?: number;
  rate_percentage?: number;
  status_type?: string;
  amount?: number;
  partial_payment_amount?: number;
  status_type_id?: number;
  monetization_status_type_id?: number;
  honored_amount?: number | null;
  confronted_amount?: number | null;
  outstanding_balance?: number | null;
  drawer?: string;
  confrontation_date?: string;
  installment_id?: number;
  badge?: BadgeStatus;
  collected_ok?: boolean;
  collected_ok_date?: string;
  collected_ok_origin?: 'MANUAL' | 'CRON';
}

interface GetOverdueColumnsProps {
  handleOpenModal: (row: OverdueData) => void;
  country?: string | null;
  sortConfig?: { field: string | null; direction: 'asc' | 'desc' | null };
}

const stickyBase: React.CSSProperties = {
  position: 'sticky',
  backgroundColor: 'inherit',
  zIndex: 1,
};

export const overdueTableCustomStyles = {
  headCells: {
    style: {
      justifyContent: 'center',
      textAlign: 'center' as const,
      whiteSpace: 'nowrap',
    },
  },
  cells: {
    style: {
      justifyContent: 'flex-end',
      textAlign: 'right' as const,
    },
  },
};

export function getOverdueColumns({
  handleOpenModal,
  country,
  sortConfig,
}: GetOverdueColumnsProps) {
  const fiscalId = getFiscalIdConfig(country);
  const columns = [
    {
      name: 'Nº Aval',
      selector: (row: OverdueData) => row.id_global,
      cell: (row: OverdueData) => (
        <P type='3' variant='neutral' withMargin={false}>
          {row.id_global}
        </P>
      ),
      minWidth: '95px',
      grow: 0,
      center: true,
      ...sortCol('id_global'),
    },
    {
      name: fiscalId.label,
      selector: (row: OverdueData) => fiscalId.format(row.cuit || ''),
      cell: (row: OverdueData) => (
        <P type='3' variant='neutral' withMargin={false}>
          {fiscalId.format(row.cuit || '')}
        </P>
      ),
      minWidth: '120px',
      grow: 0,
      ...sortCol('cuit'),
    },
    {
      name: 'Razón social',
      selector: (row: OverdueData) => row.company_name,
      cell: (row: OverdueData) => truncateCell(row.company_name),
      minWidth: '140px',
      grow: 1,
      center: true,
      ...sortCol('company_name'),
    },
    {
      name: 'Librador',
      selector: (row: OverdueData) => row.drawer || '-',
      cell: (row: OverdueData) => truncateCell(row.drawer),
      minWidth: '140px',
      grow: 1,
      center: true,
      ...sortCol('drawer'),
    },
    {
      name: 'Instrumento',
      selector: (row: OverdueData) => row.type,
      cell: (row: OverdueData) => simpleCell(row.type),
      minWidth: '140px',
      grow: 0,
      ...sortCol('type'),
    },
    {
      name: 'Acreedor',
      selector: (row: OverdueData) => row.creditor_name,
      cell: (row: OverdueData) => truncateCell(row.creditor_name),
      minWidth: '160px',
      grow: 1.4,
      center: true,
      ...sortCol('creditor_name'),
    },
    {
      name: 'Nº cuota',
      selector: (row: OverdueData) =>
        row.installment_number && row.total_installments
          ? `${row.installment_number} de ${row.total_installments}`
          : '-',
      cell: (row: OverdueData) => (
        <P type='3' variant='neutral' withMargin={false}>
          {row.installment_number && row.total_installments
            ? `${row.installment_number} de ${row.total_installments}`
            : '-'}
        </P>
      ),
      minWidth: '116px',
      grow: 0,
      center: true,
      ...sortCol('installment_number'),
    },
    {
      name: 'Vencimiento',
      selector: (row: OverdueData) => row.due_date,
      cell: (row: OverdueData) => simpleCell(formatDate(row.due_date)),
      minWidth: '132px',
      grow: 0,
      ...sortCol('due_date'),
    },
    {
      name: 'Divisa',
      selector: (row: OverdueData) => row.currency,
      cell: (row: OverdueData) => {
        const abbreviated = abbreviateCurrency(row.currency);
        const full = row.currency?.trim() || '';
        return (
          <div style={truncateWrapperStyle}>
            <Tooltip position='top' msg={full && full !== abbreviated ? full : ''}>
              <P type='3' variant='neutral' withMargin={false} style={ellipsisTextStyle}>
                {abbreviated}
              </P>
            </Tooltip>
          </div>
        );
      },
      minWidth: '92px',
      grow: 0,
      center: true,
      ...sortCol('currency'),
    },
    {
      name: 'Monto',
      selector: (row: OverdueData) => row.amount || row.monetization_amount,
      cell: (row: OverdueData) => (
        <Card width='100%' display='flex' justifyContent='flex-end'>
          <P type='3' variant='neutral' withMargin={false}>
            {formatOverdueGridAmount(row.amount || row.monetization_amount)}
          </P>
        </Card>
      ),
      minWidth: '100px',
      grow: 0,
      right: true,
      ...sortCol('amount'),
    },
    {
      name: headerWithTooltip('Afrontado', 'Monto Afrontado'),
      selector: (row: OverdueData) => row.confronted_amount ?? 0,
      cell: (row: OverdueData) => (
        <Card width='100%' display='flex' justifyContent='flex-end'>
          <P type='3' variant='neutral' withMargin={false}>
            {formatOverdueGridAmount(row.confronted_amount ?? 0)}
          </P>
        </Card>
      ),
      minWidth: '110px',
      grow: 0,
      right: true,
      ...sortCol('confronted_amount'),
    },
    {
      name: headerWithTooltip('Honrado', 'Monto Honrado'),
      selector: (row: OverdueData) => row.honored_amount ?? 0,
      cell: (row: OverdueData) => (
        <Card width='100%' display='flex' justifyContent='flex-end'>
          <P type='3' variant='neutral' withMargin={false}>
            {formatOverdueGridAmount(row.honored_amount ?? 0)}
          </P>
        </Card>
      ),
      minWidth: '100px',
      grow: 0,
      right: true,
      ...sortCol('honored_amount'),
    },
    {
      name: headerWithTooltip('Saldo', 'Saldo adeudado'),
      selector: (row: OverdueData) => row.outstanding_balance ?? 0,
      cell: (row: OverdueData) => (
        <Card width='100%' display='flex' justifyContent='flex-end'>
          <P type='3' variant='neutral' withMargin={false}>
            {formatOverdueGridAmount(row.outstanding_balance ?? 0)}
          </P>
        </Card>
      ),
      minWidth: '100px',
      grow: 0,
      right: true,
      ...sortCol('outstanding_balance'),
    },
    {
      name: 'Estado',
      selector: (row: OverdueData) => row.badge || 'PENDIENTE',
      cell: (row: OverdueData) =>
        centerCell(<OverdueBadge status={row.badge || 'PENDIENTE'} />),
      width: '160px',
      minWidth: '160px',
      grow: 0,
      center: true,
      ...sortCol('badge'),
      style: { ...stickyBase, right: '48px', borderLeft: '1px solid #E9ECEF' },
    },
    {
      name: '',
      cell: (row: OverdueData) => (
        <Card display='flex' justifyContent='center'>
          <Icon
            name='arrow_right_outline'
            variant='info'
            width={20}
            height={20}
            cursor='pointer'
            onClick={() => handleOpenModal(row)}
          />
        </Card>
      ),
      width: '48px',
      style: { ...stickyBase, right: 0 },
    },
  ];

  return columns.map((column: any) =>
    column.sortField
      ? {
          ...column,
          name: sortableHeader(
            column.name,
            sortConfig?.field === column.sortField,
            sortConfig?.direction
          ),
        }
      : column
  );
}
